/**
 * Agent of Agents — REST client.
 */

import api from '@services/api';
import type { ContextUsage } from '@/types/models';
import type {
  AoAConversation,
  AoAMessage,
  AoAProposal,
  AoAProposalResolveResponse,
  ProposalResolution,
} from '../types';

export interface AoAConfigFile {
  file_id: string;
  file_name: string;
  file_type?: string | null;
  file_size_bytes?: number | null;
  /** "Direct to context" — file content inlined into the AoA instruction
   *  (same concept as a bot attachment's include_as_context). */
  include_as_context?: boolean;
}

export interface AoAConfigBundle {
  data_bundle_id: string;
  bundle_name: string;
}

/** A Library folder attached natively to the AoA (modules.library tenants).
 *  Migrated bundle ids keep arriving as AoAConfigBundle rows. */
export interface AoAConfigLibraryFolder {
  folder_id: string;
  folder_name: string;
  file_count?: number;
}

export interface AoAConfigSkill {
  skill_id: string;
  skill_name: string;
}

/** The editable persona + attached knowledge for the tenant's Agent of Agents. */
export interface AoAFileCapabilities {
  /** The super-admin master flag (modules.agent_of_agents.file_authoring). */
  enabled: boolean;
  /** May it author new documents? */
  can_create: boolean;
  /** May it edit Library files the user can already edit? */
  can_edit_files: boolean;
  /** Formats it may author, and every format this build can render. */
  formats: string[];
  formats_available: string[];
  /** Editing acts on Library files, so on a bundle-world tenant the switch is
   *  shown but explained rather than silently doing nothing. */
  library_on: boolean;
}

export interface AoAConfig {
  /** The tenant's SAVED custom persona text (persists even while the
   *  platform default is active), or null when none was ever saved. */
  system_prompt: string | null;
  /** True when the platform default runs despite a saved custom persona. */
  use_default_persona?: boolean;
  /** Which persona actually runs right now, after hierarchy resolution. */
  active_persona?: 'default' | 'custom';
  /** Where the running persona came from. */
  persona_origin?: 'local' | 'inherited' | 'platform_default';
  persona_origin_tenant_name?: string | null;
  persona_origin_tenant_id?: string | null;
  /** This tenant's own choice: decide locally, or follow the nearest
   *  ancestor that published a persona. */
  persona_source?: 'local' | 'inherit';
  /** Offer this tenant's persona to every tenant beneath it. */
  publish_to_descendants?: boolean;
  /** What "inherit" would give this tenant — null when no ancestor publishes.
   *  `system_prompt: null` means the ancestor published the platform default. */
  inheritable_from?: {
    tenant_id: string;
    tenant_name: string;
    system_prompt: string | null;
  } | null;
  /** How far a publish from here would reach. `overriding` tenants decided
   *  for themselves, or sit under a nearer publisher. */
  descendant_reach?: { following: number; overriding: number };
  /** The shipped default persona — seeds the editor when there's no override. */
  default_system_prompt: string;
  attached_files: AoAConfigFile[];
  data_bundles: AoAConfigBundle[];
  /** What the meta-agent may do with files, and who decides. `enabled` is the
   *  super-admin master flag: when it is off the whole section is hidden,
   *  because a switch for a capability the tenant does not have is a promise
   *  the product cannot keep. Saved through PATCH /config/file-capabilities,
   *  NOT through PUT /config — files and persona are permissioned apart. */
  file_capabilities?: AoAFileCapabilities;
  /** Web search — a plain group-admin toggle, OFF by default, no master flag.
   *  Saved through PATCH /config/web-search. */
  web_search?: { enabled: boolean };
  drive_editing?: { enabled: boolean; tenant_enabled?: boolean };
  /** The meta-agent's model slot — the SAME tenant_settings key the super-admin
   *  Meta-agents tab writes. `default_model` is a family key or a release id
   *  (a platform admin may pin one); `allowed_models` [] = every family. */
  model?: {
    default_model: string;
    allowed_models: string[];
    is_default: boolean;
    platform_default: string;
  };
  /** Library folders attached directly (library_folder_ids). */
  library_folders?: AoAConfigLibraryFolder[];
  skills: AoAConfigSkill[];
  /** Whether the caller may edit (resolved modules.agent_of_agents.edit_prompt). */
  can_edit: boolean;
}

export interface AoACompactResult {
  compacted: boolean;
  reason?: string;
  error?: string;
  usage: ContextUsage;
  summary_message?: AoAMessage;
  summarized_count?: number;
  round?: number;
  tokens_before?: number;
  tokens_after?: number;
  folded_message_ids?: string[];
}

export const aoaApi = {
  async createConversation(title?: string): Promise<AoAConversation> {
    const { data } = await api.post('/agent-of-agents/conversations', { title });
    return data as AoAConversation;
  },

  async listConversations(
    opts: { limit?: number; offset?: number; q?: string } = {},
  ): Promise<{
    conversations: AoAConversation[];
    total: number;
    has_more: boolean;
  }> {
    const limit = opts.limit ?? 50;
    const offset = opts.offset ?? 0;
    const q = (opts.q ?? '').trim();
    // A search goes in a POST body, never the URL: the platform's request log
    // keeps every URL. Browsing stays a plain GET.
    const { data } = q
      ? await api.post('/agent-of-agents/conversations/search', { q, limit, offset })
      : await api.get('/agent-of-agents/conversations', { params: { limit, offset } });
    return {
      conversations: (data?.conversations ?? []) as AoAConversation[],
      total: data?.total ?? 0,
      has_more: Boolean(data?.has_more),
    };
  },

  async getConversation(id: string): Promise<AoAConversation & { messages: AoAMessage[] }> {
    const { data } = await api.get(`/agent-of-agents/conversations/${id}`);
    return data as AoAConversation & { messages: AoAMessage[] };
  },

  async deleteConversation(id: string): Promise<void> {
    await api.delete(`/agent-of-agents/conversations/${id}`);
  },

  /** The session's context meter, sized for `model` (or the model it last used). */
  async getContextUsage(id: string, model?: string): Promise<ContextUsage> {
    const { data } = await api.get(`/agent-of-agents/conversations/${id}/context-usage`, {
      params: model ? { model } : undefined,
    });
    return data.usage as ContextUsage;
  },

  /** "Compact now" for a meta-agent session. 409 NOTHING_TO_COMPACT is an
   *  answer, not an error. */
  async compactNow(id: string, model?: string): Promise<AoACompactResult> {
    try {
      const { data } = await api.post(`/agent-of-agents/conversations/${id}/compact`, model ? { model } : {});
      return data as AoACompactResult;
    } catch (error: any) {
      const payload = error?.response?.data;
      if (payload && payload.reason === 'NOTHING_TO_COMPACT') return payload as AoACompactResult;
      throw error;
    }
  },

  async getProposal(id: string): Promise<AoAProposal> {
    const { data } = await api.get(`/agent-of-agents/proposals/${id}`);
    return data as AoAProposal;
  },

  async resolveProposal(id: string, resolution: ProposalResolution): Promise<AoAProposalResolveResponse> {
    const body: Record<string, unknown> = { decision: resolution.action };
    if (resolution.action === 'edit') {
      body.edited_payload = resolution.edited_payload ?? { new_value: resolution.final_value };
    } else if (resolution.action === 'reject') {
      body.reject_reason = resolution.reason ?? '';
      // Bookkeeping rejects (closing cards the user scrolled past by
      // sending a new message) must NOT wake the agent — the message they
      // just typed is already driving the next turn.
      if (resolution.silent) body.silent = true;
    }
    const { data } = await api.post(`/agent-of-agents/proposals/${id}/resolve`, body);
    return data as AoAProposalResolveResponse;
  },

  async listProposals(conversationId?: string, status?: string): Promise<AoAProposal[]> {
    const params = new URLSearchParams();
    if (conversationId) params.append('conversation_id', conversationId);
    if (status) params.append('status', status);
    const { data } = await api.get(`/agent-of-agents/proposals?${params}`);
    return (data?.proposals ?? []) as AoAProposal[];
  },

  async getEvaluation(id: string): Promise<{
    messages: AoAMessage[];
    total_cost_usd: number;
    total_input_tokens: number;
    total_output_tokens: number;
    evaluating_chatbot_id?: string | null;
    conversation_title: string | null;
    sim_tenant_id?: string | null;
    sim_tenant_name?: string | null;
  }> {
    const { data } = await api.get(`/agent-of-agents/evaluations/${id}`);
    return data;
  },

  // ---- Simulation tenant selection ----

  /** Tenants the caller may run a simulation under (their manageable subtree
   *  + the target agent's own home tenant). Drives the panel's tenant picker. */
  async getSimulatableTenants(chatbotId?: string): Promise<Array<{
    tenant_id: string;
    tenant_name: string;
    is_bot_home: boolean;
    is_home: boolean;
    depth?: number;
    is_root_tenant?: boolean;
    hierarchy_path?: string;
    tenant_code?: string;
    parent_tenant_id?: string | null;
  }>> {
    const params: Record<string, string> = {};
    if (chatbotId) params.chatbot_id = chatbotId;
    const { data } = await api.get('/agent-of-agents/simulatable-tenants', { params });
    return (data?.tenants ?? []);
  },

  /** Start a fresh simulation (eval conversation) for an agent, bound to a
   *  chosen tenant's organizational context. Returns the new eval id + info. */
  async createEvaluation(body: {
    chatbot_id: string;
    tenant_id: string;
    meta_conversation_id?: string;
  }): Promise<{
    conversation_id: string;
    sim_tenant_id: string;
    sim_tenant_name: string;
    target_chatbot_id: string;
    target_agent_name: string;
  }> {
    const { data } = await api.post('/agent-of-agents/evaluations', body);
    return data;
  },

  // ---- Tenant-context files (file-manager card) ----

  async listTenantContextFiles(tenantId: string): Promise<any[]> {
    const { data } = await api.get(`/agent-of-agents/tenants/${tenantId}/context-files`);
    return (data?.files ?? []);
  },

  async uploadTenantContextFile(tenantId: string, file: File): Promise<any> {
    const form = new FormData();
    form.append('file', file);
    const { data } = await api.post(
      `/agent-of-agents/tenants/${tenantId}/context-files`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    // Service returns { success, attachment: {...} }.
    return data?.attachment ?? data;
  },

  async deleteTenantContextFile(tenantId: string, attachmentId: string): Promise<void> {
    await api.delete(`/agent-of-agents/tenants/${tenantId}/context-files/${attachmentId}`);
  },

  async updateTenantContextFile(
    tenantId: string,
    attachmentId: string,
    body: { include_as_context: boolean },
  ): Promise<any> {
    const { data } = await api.patch(
      `/agent-of-agents/tenants/${tenantId}/context-files/${attachmentId}`,
      body,
    );
    // Service returns { success, attachment: {...} } — return the attachment
    // so callers can read `.include_as_context` (the trust-but-verify echo).
    return data?.attachment ?? data;
  },

  // ---- Editable persona + attached knowledge (tenant AoA config) ----

  async getConfig(): Promise<AoAConfig> {
    const { data } = await api.get('/agent-of-agents/config');
    return data as AoAConfig;
  },

  async updateConfig(body: {
    system_prompt: string | null;
    use_default_persona?: boolean;
    persona_source?: 'local' | 'inherit';
    publish_to_descendants?: boolean;
    attached_file_ids: string[];
    data_bundle_ids: string[];
    /** Omit entirely to leave stored folders untouched (bundle-world UI);
     *  send the full list to overwrite. */
    library_folder_ids?: string[];
    skill_ids: string[];
  }): Promise<AoAConfig> {
    const { data } = await api.put('/agent-of-agents/config', body);
    return data as AoAConfig;
  },

  /** Save the file switches. A separate endpoint because they ride the file
   *  master flag, not `modules.agent_of_agents.edit_prompt` — a tenant may
   *  well be allowed files while its persona stays fixed. */
  async updateFileCapabilities(body: {
    file_creation_enabled?: boolean;
    file_editing_enabled?: boolean;
    file_formats?: string[];
  }): Promise<AoAFileCapabilities> {
    const { data } = await api.patch('/agent-of-agents/config/file-capabilities', body);
    return (data as { file_capabilities: AoAFileCapabilities }).file_capabilities;
  },

  /** Turn the meta-agent's web search on or off. A plain group-admin toggle,
   *  gated on modules.agent_of_agents (not the file master flag). */
  async updateWebSearch(enabled: boolean): Promise<{ enabled: boolean }> {
    const { data } = await api.patch('/agent-of-agents/config/web-search', { enabled });
    return (data as { web_search: { enabled: boolean } }).web_search;
  },

  /** The model the meta-agent starts a conversation on, for this tenant. A
   *  family key; null resets to the platform default. Same gate as the persona
   *  editor; writes the key the super-admin Meta-agents tab edits. */
  async updateDefaultModel(defaultModel: string | null): Promise<NonNullable<AoAConfig['model']>> {
    const { data } = await api.patch('/agent-of-agents/config/default-model', { default_model: defaultModel });
    return (data as { model: NonNullable<AoAConfig['model']> }).model;
  },

  /** The meta-agent's level-2 Drive switch: may it rewrite existing Drive
   *  files in place? Default off. Only takes effect while the tenant flag
   *  `integrations.cloud_storage.editing` is on (`tenant_enabled`). */
  async updateDriveEditing(enabled: boolean): Promise<{ enabled: boolean; tenant_enabled?: boolean }> {
    const { data } = await api.patch('/agent-of-agents/config/drive-editing', { enabled });
    return (data as { drive_editing: { enabled: boolean; tenant_enabled?: boolean } }).drive_editing;
  },

  /** The composer branding-toggle state for one conversation: whether branded
   *  rendering is live for the tenant, which brandable formats the meta-agent
   *  may create, and the per-format opt-out map so far. Same control the
   *  regular agent has — a document the meta-agent writes wears the tenant's
   *  design skill unless a format is switched off here. */
  async getArtifactBrand(conversationId: string): Promise<{
    branded_available: boolean;
    formats: string[];
    branding: Record<string, boolean>;
  }> {
    const { data } = await api.get(
      `/agent-of-agents/conversations/${conversationId}/artifact-brand`);
    return data as { branded_available: boolean; formats: string[]; branding: Record<string, boolean> };
  },

  /** Opt one format in or out of branded rendering for this conversation. */
  async setArtifactBranding(
    conversationId: string, format: string, branded: boolean,
  ): Promise<Record<string, boolean>> {
    const { data } = await api.patch(
      `/agent-of-agents/conversations/${conversationId}/artifact-branding`,
      { format, branded });
    return (data as { branding: Record<string, boolean> }).branding || {};
  },

  /** Whether this conversation auto-accepts proposals (deletes always wait). */
  async getAutoAccept(conversationId: string): Promise<boolean> {
    const { data } = await api.get(
      `/agent-of-agents/conversations/${conversationId}/auto-accept`);
    return Boolean((data as { enabled: boolean }).enabled);
  },

  /** Turn auto-accept on or off for this conversation. */
  async setAutoAccept(conversationId: string, enabled: boolean): Promise<boolean> {
    const { data } = await api.patch(
      `/agent-of-agents/conversations/${conversationId}/auto-accept`, { enabled });
    return Boolean((data as { enabled: boolean }).enabled);
  },

  async uploadConfigFile(file: File): Promise<AoAConfig> {
    const form = new FormData();
    form.append('file', file);
    const { data } = await api.post('/agent-of-agents/config/files', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data as AoAConfig;
  },

  async deleteConfigFile(fileId: string): Promise<AoAConfig> {
    const { data } = await api.delete(`/agent-of-agents/config/files/${fileId}`);
    return data as AoAConfig;
  },

  /** Toggle "direct to context" for one attached config file. */
  async setConfigFileContext(fileId: string, includeAsContext: boolean): Promise<AoAConfig> {
    const { data } = await api.patch(`/agent-of-agents/config/files/${fileId}`, {
      include_as_context: includeAsContext,
    });
    return data as AoAConfig;
  },
};
